<template lang="pug">
Teleport(to="body")
  Transition(name="modal")
    .modal(
      v-if="visible"
      @click.self="handleOverlayClick"
    )
      .modal__overlay(@click.self="handleOverlayClick")
        section.modal__content(
          ref="modalContent"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          :aria-describedby="descriptionId"
          @keydown="handleKeyDown"
        )
          button.modal__close(
            aria-label="Close dialog"
            type="button"
            @click="closeModal"
          ) ✖

          h2.modal__title(
            v-if="title"
            :id="titleId"
          ) {{ title }}

          .modal__body(:id="descriptionId")
            slot
</template>

<script setup lang="ts">
  import { useId, useTemplateRef } from 'vue'

  /**
   * Accessible Modal Component
   *
   * WCAG 2.1 Features:
   * - Focus trap: Keyboard navigation contained within modal
   * - Escape key handler: Close modal with Esc key
   * - ARIA attributes: role="dialog", aria-modal, aria-labelledby
   * - Focus management: Returns focus to trigger element on close
   * - Overlay click: Close modal by clicking outside content area
   */

  //
  // Props and events
  //

  interface ModalProps {
    /** Dialog title announced by assistive technology. */
    title?: string
    /** Whether the modal is rendered. */
    visible: boolean
    /** Whether overlay clicks close the modal. */
    closeOnOverlay?: boolean
    /** Whether Escape key closes the modal. */
    closeOnEscape?: boolean
  }

  const props = withDefaults(defineProps<ModalProps>(), {
    title: undefined,
    closeOnOverlay: true,
    closeOnEscape: true,
  })

  const emit = defineEmits<{
    close: []
  }>()

  //
  // Composables
  //

  const modalContent = useTemplateRef<HTMLElement>('modalContent')

  //
  // Data
  //

  let previousActiveElement: HTMLElement | null = null
  let focusableElements: HTMLElement[] = []
  let firstFocusable: HTMLElement | null = null
  let lastFocusable: HTMLElement | null = null

  // Generate unique IDs for ARIA

  //
  // Composables
  //

  const uniqueId = useId()

  //
  // Data
  //

  const titleId = `modal-title-${uniqueId}`
  const descriptionId = `modal-description-${uniqueId}`

  /**
   * Get all focusable elements within the modal
   */

  //
  // Methods
  //

  function getFocusableElements(): HTMLElement[] {
    if (!modalContent.value) return []

    const selectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ')

    return Array.from(modalContent.value.querySelectorAll(selectors)) as HTMLElement[]
  }

  /**
   * Trap focus within modal (prevent Tab from leaving modal)
   */
  function trapFocus(event: KeyboardEvent) {
    if (event.key !== 'Tab') return

    // Update focusable elements (in case DOM changed)
    focusableElements = getFocusableElements()
    if (focusableElements.length === 0) return

    firstFocusable = focusableElements[0] || null
    lastFocusable = focusableElements[focusableElements.length - 1] || null

    // Shift + Tab (backwards)
    if (event.shiftKey) {
      if (document.activeElement === firstFocusable) {
        event.preventDefault()
        lastFocusable?.focus()
      }
    }
    // Tab (forwards)
    else {
      if (document.activeElement === lastFocusable) {
        event.preventDefault()
        firstFocusable?.focus()
      }
    }
  }

  /**
   * Handle Escape key to close modal
   */
  function handleEscape(event: KeyboardEvent) {
    if (event.key === 'Escape' && props.closeOnEscape) {
      event.preventDefault()
      closeModal()
    }
  }

  /**
   * Handle all keyboard events
   */
  function handleKeyDown(event: KeyboardEvent) {
    trapFocus(event)
    handleEscape(event)
  }

  /**
   * Handle clicking on overlay (outside modal content)
   */
  function handleOverlayClick() {
    if (props.closeOnOverlay) {
      closeModal()
    }
  }

  /**
   * Close modal and emit event
   */
  function closeModal() {
    emit('close')
  }

  /**
   * Set initial focus when modal opens
   */
  function setInitialFocus() {
    nextTick(() => {
      focusableElements = getFocusableElements()

      if (focusableElements.length > 0) {
        // Focus first focusable element (usually close button)
        firstFocusable = focusableElements[0] || null
        lastFocusable = focusableElements[focusableElements.length - 1] || null
        firstFocusable?.focus()
      } else if (modalContent.value) {
        // No focusable elements, focus modal itself
        modalContent.value.setAttribute('tabindex', '-1')
        modalContent.value.focus()
      }
    })
  }

  /**
   * Restore focus to previously focused element
   */
  function restoreFocus() {
    if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
      previousActiveElement.focus()
    }
  }

  /**
   * Watch for visibility changes
   */

  //
  // Watchers
  //

  watch(
    () => props.visible,
    newValue => {
      if (newValue) {
        // Save currently focused element
        previousActiveElement = document.activeElement as HTMLElement

        // Set initial focus in modal
        setInitialFocus()

        // Prevent body scroll
        document.body.style.overflow = 'hidden'
      } else {
        // Restore focus to previous element
        restoreFocus()

        // Restore body scroll
        document.body.style.overflow = ''
      }
    },
  )

  /**
   * Cleanup on unmount
   */

  //
  // Lifecycle Hooks
  //

  onUnmounted(() => {
    if (props.visible) {
      document.body.style.overflow = ''
    }
  })
</script>

<style lang="scss" scoped>
  .modal {
    position: relative;
    z-index: 1000;
  }

  .modal__overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .modal__content {
    background-color: color(white);
    border-radius: $border-radius-big;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 1);
    max-width: 36rem;
    width: 90%;
    padding: double();
    position: relative;
  }

  .modal__close {
    position: absolute;
    top: simple();
    right: simple();
    background: none;
    border: none;
    font-size: 0.8rem;
    color: color(gray6);
    cursor: pointer;
    width: double();
    height: double();
    display: flex;
    justify-content: center;
    align-items: center;

    &:hover {
      color: color(gray1);
      background-image: none;
    }
  }

  @include media(palm) {
    .modal__content {
      padding: simple();
    }
  }
</style>
